import { Typography } from "@creditsafe/ui-components";
import { Box, Grid, CircularProgress } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useGetRechargeSummary } from "../../../api/recharge";
import { formatDate } from "../../../utils";

const MetricsContainer = styled(Box)`
  padding: 1rem;
  margin-top: 1rem;
  margin-bottom: 1rem;
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-top: 1px solid ${(props) => props.theme.primaryColor};
  width: 100%;
`;

const MetricItem = styled(Grid)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  border-right: 1px solid #e0e0e0;
  text-align: center;

  &:last-child {
    border-right: none;
  }
`;

const MetricText = styled(Typography)`
  color: #000;
  white-space: nowrap;
  text-align: center;
  width: 100%;
`;

const LoadingContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
`;

interface RechargeMetricsProps {
  isVisible: boolean;
}

const RechargeMetrics: React.FC<RechargeMetricsProps> = ({ isVisible }) => {
  const { t: translate } = useTranslation(["common", "profile"]);
  const { data, isLoading, isError } = useGetRechargeSummary(isVisible);

  if (!isVisible) {
    return null;
  }

  if (isLoading) {
    return (
      <MetricsContainer>
        <LoadingContainer>
          <CircularProgress size={24} aria-label="loading metrics" />
        </LoadingContainer>
      </MetricsContainer>
    );
  }

  if (isError || data?.apiFailed) {
    return null;
  }

  const formattedDate = data?.rechargeDueDate
    ? formatDate(data.rechargeDueDate, "en-GB", "P")
    : translate("common:notAvailable");

  const displayScreeningCredits =
    data?.availableScreeningCredits === "unlimited"
      ? translate("common:notApplicable")
      : data?.availableScreeningCredits ?? 0;

  const displayMonitoringCredits =
    data?.availableMonitoringCredits === "unlimited"
      ? translate("common:notApplicable")
      : data?.availableMonitoringCredits ?? 0;

  return (
    <MetricsContainer>
      <Grid container>
        <MetricItem item xs={12} sm={6} md={3}>
          <MetricText variant="body1">
            {translate("common:rechargeMetrics.availableScreeningCredits")}:{" "}
            <strong>{displayScreeningCredits}</strong>
          </MetricText>
        </MetricItem>
        <MetricItem item xs={12} sm={6} md={3}>
          <MetricText variant="body1">
            {translate("common:rechargeMetrics.availableMonitoringCredits")}:{" "}
            <strong>{displayMonitoringCredits}</strong>
          </MetricText>
        </MetricItem>
        <MetricItem item xs={12} sm={6} md={3}>
          <MetricText variant="body1">
            {translate("common:rechargeMetrics.monitoringRechargeDueOn")}:{" "}
            <strong>{formattedDate}</strong>
          </MetricText>
        </MetricItem>
        <MetricItem item xs={12} sm={6} md={3}>
          <MetricText variant="body1">
            {translate("common:rechargeMetrics.minimumTopupForNextRenewal")}:{" "}
            <strong>{data?.minTopupToRenew ?? 0}</strong>
          </MetricText>
        </MetricItem>
      </Grid>
    </MetricsContainer>
  );
};

export default RechargeMetrics;
